/**
 * The Holdings tab's arithmetic — pure, so every rule here is a test.
 * The design is `docs/mockups/holdings-13f.html`; the data is `api/holdings.ts`.
 */
import type { Holding, HoldingsSymbol, LedgerMove, LedgerQuarter } from "../api/holdings";
import { useEffect, useState } from "react";
import { fetchHoldings } from "../api/holdings";

/**
 * Fund identity colours, by the fund's fixed order (1 = Point72 … 15 = Berkshire).
 *
 * **A palette here, not theme tokens, deliberately**: a theme has six series
 * colours and there are fifteen funds. These name a fund — the same fund is the
 * same colour in the quarter bars and in the holder list beside them — and carry
 * no meaning a theme should re-tint; added, cut and pending use
 * `--qf-positive`, `--qf-negative` and `--qf-warn`.
 */
export const FUND_COLORS = [
  "#5c7cfa", "#26c6da", "#ffb74d", "#ab47bc", "#9ccc65", "#ec407a", "#8d6e63", "#4fc3f7",
  "#ff7043", "#d4e157", "#7e57c2", "#26a69a", "#f06292", "#a1887f", "#90a4ae",
] as const;

export function fundColor(order: number | null | undefined): string {
  if (!order || order < 1) return "#78909c";
  return FUND_COLORS[(order - 1) % FUND_COLORS.length] ?? "#78909c";
}

/** 13Fs are due 45 days after the quarter ends. */
export function dueDate(qn: string): Date {
  const d = new Date(`${qn}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 45);
  return d;
}

export function shortDate(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** "Q2 26" from a quarter-end date. */
export function quarterLabel(period: string): string {
  const [y = 0, m = 1] = period.split("-").map(Number);
  return `Q${Math.ceil(m / 3)} ${String(y % 100).padStart(2, "0")}`;
}

/**
 * A filer whose share book is under this part of its whole 13F is flagged
 * wherever its "% of book" shows: the rest is options, so the percentage is of
 * its stock only (owner, 2026-10-13). The server's `derive.OPTIONS_HEAVY`.
 */
export const OPTIONS_HEAVY = 0.5;

export function optionsHeavy(stockShare: number | null | undefined): boolean {
  return stockShare !== null && stockShare !== undefined && stockShare < OPTIONS_HEAVY;
}

/** The tooltip on a flagged "of book". */
export function optionsNote(stockShare: number): string {
  return `options are ${pct(1 - stockShare)} of this filer's 13F — the % is of its stock only`;
}

/** `$25.6B`, `−$367M`, `$5K` — the sign kept, magnitude compact. */
export function usd(v: number | null | undefined, signed = false): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  const s = v < 0 ? "−" : signed && v > 0 ? "+" : "";
  if (a >= 1e12) return `${s}$${(a / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(a >= 1e10 ? 1 : 2)}B`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(0)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(0)}K`;
  return `${s}$${a.toFixed(0)}`;
}

/** A small share as `<0.01%`, a large one with fewer decimals. */
export function pct(fraction: number | null | undefined, signed = false): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return "—";
  const p = fraction * 100;
  const s = signed && p > 0 ? "+" : p < 0 ? "−" : "";
  const a = Math.abs(p);
  if (a > 0 && a < 0.01) return `${s}<0.01%`;
  return `${s}${a < 1 ? a.toFixed(2) : a < 10 ? a.toFixed(1) : a.toFixed(0)}%`;
}

/**
 * The change in a holder's share count, from dollars: `trade ÷ (value − trade)`
 * is the trade against the earlier position at today's price — split-safe for
 * the reason `trade` is. A ten-fold or larger change reads as a multiple
 * (`×901`): Fisher's GEV went from 3,949 shares to 3.56M.
 */
export function shareChange(trade: number | null, value: number, status: string | null): string {
  if (status === "NEW") return "NEW";
  if (status === "EXIT") return "EXIT";
  if (trade === null) return "—";
  const before = value - trade;
  if (before <= 0) return "—";
  const r = trade / before;
  if (r >= 9) return `×${(r + 1).toFixed(0)}`;
  return pct(r, true);
}

/** One fund's segment in a quarter's bar, as a share of that quarter's larger side. */
export interface Segment {
  fund: string;
  fundName: string;
  color: string;
  width: number;          // 0..1 of the half-bar
  trade: number;
  status: LedgerMove["status"];
}

/**
 * Each bar is scaled to **its own quarter** (owner, 2026-10-12): on one scale a
 * $5.4B quarter turns every other quarter into slivers, and the bar's job is to
 * show who drove the quarter. The size is in the net beside it.
 */
export function quarterSegments(q: LedgerQuarter, orderOf: (fund: string) => number | null): { adds: Segment[]; cuts: Segment[] } {
  const scale = Math.max(q.bought, q.sold, 1);
  const seg = (m: LedgerMove): Segment => ({
    fund: m.fund, fundName: m.fundName, color: fundColor(orderOf(m.fund)),
    width: Math.abs(m.trade) / scale, trade: m.trade, status: m.status,
  });
  return { adds: q.adds.map(seg), cuts: q.cuts.map(seg) };
}

/** The price move into a quarter, from its close and the one before. */
export function priceMove(ledger: LedgerQuarter[], i: number): number | null {
  const q = ledger[i], prev = ledger[i + 1];
  if (!q?.close || !prev?.close) return null;
  return q.close / prev.close - 1;
}

/** Totals and the base quarter's change, for the summary box. */
export interface Summary {
  heldValue: number;
  holders: number;
  sharesOutPct: number | null;
  baseNet: number;
  baseIn: number;
  baseOut: number;
}

export function summary(h: HoldingsSymbol): Summary {
  const base = h.ledger.find((q) => q.period === h.window.base);
  const moves = base ? [...base.adds, ...base.cuts] : [];
  const out = h.holders.map((x) => x.pctOut).filter((v): v is number => v !== null);
  return {
    heldValue: h.holders.reduce((s, x) => s + x.value, 0),
    holders: h.holders.length,
    sharesOutPct: out.length ? out.reduce((s, v) => s + v, 0) : null,
    baseNet: base ? base.bought - base.sold : 0,
    baseIn: moves.filter((m) => m.status === "NEW").length,
    baseOut: moves.filter((m) => m.status === "EXIT").length,
  };
}

/** A holder's trade in a given quarter, from the ledger (the row itself is its latest filing). */
export function tradeIn(h: HoldingsSymbol, fund: string, period: string): LedgerMove | undefined {
  const q = h.ledger.find((x) => x.period === period);
  return q ? [...q.adds, ...q.cuts].find((m) => m.fund === fund) : undefined;
}

/** Whether a holder has filed the newest quarter yet. */
export function filedNewest(holder: Holding, h: HoldingsSymbol): boolean {
  return holder.period === h.window.qn;
}

// ── the hook ──────────────────────────────────────────────────────────────
const cache = new Map<string, Promise<HoldingsSymbol | undefined>>();

/** Only a successful answer is kept: a 503 before the first pull must be retried. */
export function loadHoldings(symbol: string): Promise<HoldingsSymbol | undefined> {
  if (!symbol) return Promise.resolve(undefined);
  const id = symbol.toUpperCase();
  const existing = cache.get(id);
  if (existing) return existing;
  const pending = fetchHoldings(id).then((r) => {
    if (r === undefined) cache.delete(id);
    return r;
  });
  cache.set(id, pending);
  return pending;
}

export type HoldingsLoad = { state: "loading" } | { state: "none" } | { state: "ready"; data: HoldingsSymbol };

export function useHoldings(symbol: string): HoldingsLoad {
  const [load, setLoad] = useState<HoldingsLoad>({ state: "loading" });
  useEffect(() => {
    let live = true;
    setLoad({ state: "loading" });
    void loadHoldings(symbol).then((r) => {
      if (live) setLoad(r ? { state: "ready", data: r } : { state: "none" });
    });
    return () => {
      live = false;
    };
  }, [symbol]);
  return load;
}

// ── the holder rows' quarter columns ──────────────────────────────────────
/** The quarters a holder row shows, oldest first: every one with a quarter before it. */
export function holderQuarters(h: HoldingsSymbol): string[] {
  return h.quarters.slice(1);
}

export type QuarterCell =
  | { kind: "pending" }                                   // not filed yet — never $0
  | { kind: "move"; trade: number; status: LedgerMove["status"]; shares: string }
  | { kind: "held" }                                      // held, no trade worth a row
  | { kind: "none" };                                     // not held that quarter

/** Whether a fund has filed a quarter, as far as this symbol's data can say. */
function filed(h: HoldingsSymbol, fund: string, period: string): boolean {
  if (period !== h.window.qn) return true;
  return [...h.holders, ...h.exited, ...h.optionsOnly].some((x) => x.fund === fund && x.period === period);
}

export function quarterCell(h: HoldingsSymbol, fund: string, period: string): QuarterCell {
  if (!filed(h, fund, period)) return { kind: "pending" };
  const i = h.quarters.indexOf(period);
  const value = h.history[fund]?.[i] ?? 0;
  const m = tradeIn(h, fund, period);
  if (m) return { kind: "move", trade: m.trade, status: m.status, shares: shareChange(m.trade, value, m.status) };
  return value > 0 ? { kind: "held" } : { kind: "none" };
}

/** Funds that held the symbol at some point in the year and hold nothing now. */
export function earlierHolders(h: HoldingsSymbol): string[] {
  const now = new Set([...h.holders, ...h.exited].map((x) => x.fund));
  return Object.entries(h.history)
    .filter(([fund, row]) => !now.has(fund) && row.some((v) => (v ?? 0) > 0))
    .map(([fund]) => fund);
}
